/**
 * Business-time helpers (pure, no React, no browser-timezone dependency).
 *
 * Temporal contract:
 * - INSTANT: absolute moment (createdAt, paidAt, openedAt…). Display it in the
 *   BUSINESS timezone (tenant.timezone) → formatBusinessDateTime / Date / Time.
 * - LOCAL_DATE: calendar date without time (fiscal expirationDate, lot expiry,
 *   dueDate, validUntil, subscription periods…). Stored as UTC midnight of that
 *   date → formatLocalDate (date part only, never converted to local).
 * - BUSINESS_DAY: "hoy", "ayer", "este mes", date pickers → "YYYY-MM-DD" keys
 *   computed in the tenant timezone → businessDateKey / addDaysToDateKey. The
 *   backend turns keys + tenant.timezone into instant ranges.
 *
 * The browser timezone is never an authority for business data: every function
 * here takes the timezone explicitly and only uses Intl with `timeZone`, or UTC
 * getters/Date.UTC for calendar math.
 */

import { format as formatDateFns } from "date-fns";
import { es } from "date-fns/locale";

export const DEFAULT_BUSINESS_TIME_ZONE = "America/Tegucigalpa";
export const BUSINESS_LOCALE = "es-HN";

export type InstantLike = Date | string | number;
export type LocalDateLike = Date | string;

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/;

const validTimeZoneCache = new Map<string, boolean>();

export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== "string") return false;
  const trimmed = tz.trim();
  if (!trimmed || trimmed !== tz) return false;
  const cached = validTimeZoneCache.get(tz);
  if (cached !== undefined) return cached;
  let valid = false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(0);
    valid = true;
  } catch {
    valid = false;
  }
  validTimeZoneCache.set(tz, valid);
  return valid;
}

export function resolveBusinessTimeZone(tz?: string | null): string {
  return isValidTimeZone(tz) ? tz : DEFAULT_BUSINESS_TIME_ZONE;
}

/** Parses an INSTANT-like value. Returns null for empty/invalid input. */
export function toInstant(instant: InstantLike | null | undefined): Date | null {
  if (instant === null || instant === undefined || instant === "") return null;
  const date = instant instanceof Date ? instant : new Date(instant);
  return Number.isNaN(date.getTime()) ? null : date;
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getPartsFormatter(tz: string): Intl.DateTimeFormat {
  let formatter = partsFormatterCache.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      calendar: "gregory",
      numberingSystem: "latn",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    partsFormatterCache.set(tz, formatter);
  }
  return formatter;
}

export interface BusinessDateTimeParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

/** Wall-clock parts of an instant in the given business timezone. */
export function getBusinessDateTimeParts(
  instant: InstantLike,
  tz?: string | null,
): BusinessDateTimeParts | null {
  const date = toInstant(instant);
  if (!date) return null;
  const map: Record<string, string> = {};
  for (const part of getPartsFormatter(resolveBusinessTimeZone(tz)).formatToParts(date)) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  const hour = Number(map.hour);
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    // Some engines render midnight as "24" even with h23.
    hour: hour === 24 ? 0 : hour,
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, "0");
}

function keyFromYmd(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** "YYYY-MM-DD" business date of `instant` (default: now) in the tenant timezone. */
export function businessDateKey(
  instant: InstantLike | null | undefined = new Date(),
  tz?: string | null,
): string {
  const parts = getBusinessDateTimeParts(instant ?? new Date(), tz);
  if (!parts) return "";
  return keyFromYmd(parts.year, parts.month, parts.day);
}

/** Today's business date key in the tenant timezone. */
export function todayBusinessDateKey(tz?: string | null, now: InstantLike = new Date()): string {
  return businessDateKey(now, tz);
}

export function isDateKey(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = DATE_KEY_RE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return utc.getUTCFullYear() === y && utc.getUTCMonth() === m - 1 && utc.getUTCDate() === d;
}

function parseDateKey(key: string): [number, number, number] {
  const match = DATE_KEY_RE.exec(key);
  if (!match) throw new Error(`Invalid date key: ${key}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/** Pure calendar math on "YYYY-MM-DD" keys (no timezone, no DST). */
export function addDaysToDateKey(key: string, days: number): string {
  const [y, m, d] = parseDateKey(key);
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  return keyFromYmd(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
}

/** Whole days from `fromKey` to `toKey` (positive when `toKey` is later). */
export function diffDateKeysInDays(fromKey: string, toKey: string): number {
  const [fy, fm, fd] = parseDateKey(fromKey);
  const [ty, tm, td] = parseDateKey(toKey);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** Day of week of a date key: 0 = Sunday … 6 = Saturday. */
export function dayOfWeekOfDateKey(key: string): number {
  const [y, m, d] = parseDateKey(key);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Monday of the ISO week containing `key`. */
export function startOfBusinessWeekKey(key: string): string {
  return addDaysToDateKey(key, -((dayOfWeekOfDateKey(key) + 6) % 7));
}

export function startOfBusinessMonthKey(key: string): string {
  const [y, m] = parseDateKey(key);
  return keyFromYmd(y, m, 1);
}

export function startOfBusinessYearKey(key: string): string {
  const [y] = parseDateKey(key);
  return keyFromYmd(y, 1, 1);
}

/** Last day of the previous month and first day of it, as keys. */
export function previousBusinessMonthRange(key: string): { from: string; to: string } {
  const to = addDaysToDateKey(startOfBusinessMonthKey(key), -1);
  return { from: startOfBusinessMonthKey(to), to };
}

const DEFAULT_DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const DEFAULT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};
const DEFAULT_TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
};

/** Same output as `Date#toLocaleString(locale)` defaults, for drop-in use with a timeZone. */
export const TO_LOCALE_STRING_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};
/** Same output as `Date#toLocaleDateString(locale)` defaults. */
export const TO_LOCALE_DATE_STRING_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};
/** Same output as `Date#toLocaleTimeString(locale)` defaults. */
export const TO_LOCALE_TIME_STRING_OPTS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
};

function formatInstant(
  instant: InstantLike | null | undefined,
  tz: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  const date = toInstant(instant);
  if (!date) return "";
  return new Intl.DateTimeFormat(BUSINESS_LOCALE, {
    ...opts,
    timeZone: resolveBusinessTimeZone(tz),
  }).format(date);
}

/** INSTANT → date + time in the business timezone. `opts` replaces the defaults. */
export function formatBusinessDateTime(
  instant: InstantLike | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  return formatInstant(instant, tz, opts ?? DEFAULT_DATE_TIME_OPTS);
}

/** INSTANT → date (as seen in the business timezone). `opts` replaces the defaults. */
export function formatBusinessDate(
  instant: InstantLike | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  return formatInstant(instant, tz, opts ?? DEFAULT_DATE_OPTS);
}

/** INSTANT → time in the business timezone. `opts` replaces the defaults. */
export function formatBusinessTime(
  instant: InstantLike | null | undefined,
  tz?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  return formatInstant(instant, tz, opts ?? DEFAULT_TIME_OPTS);
}

/**
 * LOCAL_DATE → "YYYY-MM-DD" (the stored calendar date). Accepts a date key,
 * an ISO string (its date prefix is taken verbatim) or a Date (UTC parts).
 */
export function localDatePart(dateLike: LocalDateLike | null | undefined): string | null {
  if (dateLike === null || dateLike === undefined || dateLike === "") return null;
  if (typeof dateLike === "string") {
    const match = DATE_PREFIX_RE.exec(dateLike.trim());
    if (match) {
      const key = `${match[1]}-${match[2]}-${match[3]}`;
      return isDateKey(key) ? key : null;
    }
    const parsed = new Date(dateLike);
    if (Number.isNaN(parsed.getTime())) return null;
    return keyFromYmd(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, parsed.getUTCDate());
  }
  if (Number.isNaN(dateLike.getTime())) return null;
  return keyFromYmd(dateLike.getUTCFullYear(), dateLike.getUTCMonth() + 1, dateLike.getUTCDate());
}

/**
 * LOCAL_DATE (or a business date key) → formatted date only, never shifted by
 * any timezone. `opts` replaces the defaults (no time fields are honoured).
 */
export function formatLocalDate(
  dateLike: LocalDateLike | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const key = localDatePart(dateLike);
  if (!key) return "";
  const [y, m, d] = parseDateKey(key);
  const source = opts ?? DEFAULT_DATE_OPTS;
  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: source.weekday,
    era: source.era,
    year: source.year,
    month: source.month,
    day: source.day,
    dateStyle: source.dateStyle,
  };
  return new Intl.DateTimeFormat(BUSINESS_LOCALE, {
    ...dateOpts,
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/**
 * Returns a Date whose LOCAL fields equal the business wall clock, for use
 * ONLY with date-fns `format()` patterns (date-fns v3 has no timezone
 * support). Never send it to the API or compare it with real instants.
 * Caveat: a wall-clock time that does not exist in the viewer's own zone (DST
 * gap) is shifted by the engine; business zones in Central America have no DST.
 */
export function toBusinessWallClock(
  instant: InstantLike | null | undefined,
  tz?: string | null,
): Date | null {
  const date = toInstant(instant);
  if (!date) return null;
  const parts = getBusinessDateTimeParts(date, tz);
  if (!parts) return null;
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds(),
  );
}

/** Shown before saving a tenant timezone change. */
export const TIMEZONE_CHANGE_WARNING =
  "Cambiar la zona horaria no modifica las ventas registradas, pero cambia cómo se agrupan por día los reportes, incluidos los históricos.";

/** Common IANA zones offered in timezone pickers (validated at runtime). */
export const COMMON_BUSINESS_TIME_ZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "America/Tegucigalpa", label: "Honduras (Tegucigalpa)" },
  { value: "America/Guatemala", label: "Guatemala" },
  { value: "America/El_Salvador", label: "El Salvador" },
  { value: "America/Managua", label: "Nicaragua (Managua)" },
  { value: "America/Costa_Rica", label: "Costa Rica" },
  { value: "America/Panama", label: "Panamá" },
  { value: "America/Belize", label: "Belice" },
  { value: "America/Mexico_City", label: "México (Ciudad de México)" },
  { value: "America/Bogota", label: "Colombia (Bogotá)" },
  { value: "America/Lima", label: "Perú (Lima)" },
  { value: "America/Santiago", label: "Chile (Santiago)" },
  { value: "America/New_York", label: "EE. UU. Este (Nueva York)" },
  { value: "America/Los_Angeles", label: "EE. UU. Pacífico (Los Ángeles)" },
  { value: "Europe/Madrid", label: "España (Madrid)" },
  { value: "UTC", label: "UTC" },
];

/**
 * INSTANT → date-fns `format()` pattern rendered on the business wall clock
 * (Spanish locale). Use to keep existing date-fns patterns while displaying in
 * the tenant timezone. Returns "" for empty/invalid instants.
 */
export function formatBusinessPattern(
  instant: InstantLike | null | undefined,
  tz: string | null | undefined,
  pattern: string,
): string {
  const wall = toBusinessWallClock(instant, tz);
  return wall ? formatDateFns(wall, pattern, { locale: es }) : "";
}
